import { useState, useEffect, useCallback, useRef } from 'react'
import { phoneVerificationService } from '@/services/phoneVerificationService'
import { useAuth } from '@/hooks/useAuth'

export function usePhoneVerification() {
  const { user, isAuthenticated } = useAuth()
  const [showPrompt, setShowPrompt] = useState(false)
  const [hasPhone, setHasPhone] = useState<boolean | null>(null)
  const [phoneVerified, setPhoneVerified] = useState<boolean | null>(null)
  const [loading, setLoading] = useState(false)
  const checkedRef = useRef(false)

  const checkStatus = useCallback(async () => {
    if (!isAuthenticated || !user) return

    setLoading(true)
    try {
      const status = await phoneVerificationService.getStatus()
      const verified = status.hasPhone && status.phoneVerified === true
      setHasPhone(status.hasPhone)
      setPhoneVerified(status.phoneVerified ?? false)

      if (!verified && phoneVerificationService.shouldShowPrompt()) {
        setShowPrompt(true)
      }
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated, user])

  useEffect(() => {
    if (checkedRef.current || !isAuthenticated || !user) return
    checkedRef.current = true

    checkStatus()
  }, [isAuthenticated, user, checkStatus])

  const dismissPrompt = useCallback(() => {
    phoneVerificationService.markPromptShown()
    setShowPrompt(false)
  }, [])

  const markVerified = useCallback((phoneNumber: string, verified: boolean = true) => {
    setHasPhone(true)
    setPhoneVerified(verified)
    phoneVerificationService.markPromptShown()
    setShowPrompt(false)
  }, [])

  const needsVerification = hasPhone === false || (hasPhone === true && phoneVerified !== true)

  return {
    showPrompt,
    hasPhone,
    phoneVerified,
    needsVerification,
    loading,
    checkStatus,
    dismissPrompt,
    markVerified,
  }
}
